import { CheckCircleIcon, DeleteIcon } from "@chakra-ui/icons";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Image,
  Link,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Select,
  Spinner as ChakraSpinner,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { franchiseMapIndex, titleMapIndex } from "../../../core/constants";
import {
  createAdminReadingOrderFromRawScraps,
  deleteAdminRawScraps,
  getAdminComics,
  getAdminRawScraps,
  matchAdminRawScrap,
  rematchAdminRawScraps,
} from "../../../services/adminApi";
import Spinner from "../../../shared/components/Spinner";

const isFullyMatched = (scrap) =>
  scrap.totalCount > 0 && scrap.matchedCount === scrap.totalCount;

const ScrapSummary = ({ scrap }) => (
  <Flex
    align={{ base: "stretch", md: "center" }}
    direction={{ base: "column", md: "row" }}
    flex="1"
    gap="4"
    textAlign="left"
  >
    <Box flex="1">
      <Heading as="h3" fontSize="md">
        {scrap.cmroEntityName || "Untitled CMRO order"}
      </Heading>
      <Text color="gray.500" fontSize="sm">
        CMRO entity #{scrap.cmroEntityId}
      </Text>
    </Box>
    <Box minW={{ md: "260px" }}>
      <Flex justify="space-between" mb="1">
        <Text fontSize="sm">Matched</Text>
        <Text fontSize="sm" fontWeight="semibold">
          {scrap.matchedCount}/{scrap.totalCount} ({scrap.matchPercentage}%)
        </Text>
      </Flex>
      <Progress
        aria-label={`${scrap.cmroEntityName} match progress`}
        colorScheme={isFullyMatched(scrap) ? "green" : "orange"}
        hasStripe={!isFullyMatched(scrap)}
        value={scrap.matchPercentage}
      />
    </Box>
  </Flex>
);

const CompletedScrapForm = ({ scrap }) => {
  const [title, setTitle] = useState(scrap.cmroEntityName || "");
  const [franchise, setFranchise] = useState("");
  const [type, setType] = useState("");
  const [createdReadingOrder, setCreatedReadingOrder] = useState(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const createMutation = useMutation((values) =>
    createAdminReadingOrderFromRawScraps(scrap.cmroEntityId, values)
  );

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      const response = await createMutation.mutateAsync({
        title,
        franchise,
        type,
      });

      setCreatedReadingOrder(response.data);
      queryClient.invalidateQueries(["readingOrders"]);
      await queryClient.invalidateQueries(["adminRawScraps"]);
      toast({
        title: "Reading order created.",
        description: `${title} was created as a draft with ${scrap.totalCount} comics.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Unable to create reading order.",
        description:
          error.response?.data?.message ||
          "Please check the form and try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (createdReadingOrder) {
    return (
      <Alert status="success" mt="5">
        <CheckCircleIcon mr="3" />
        <AlertDescription flex="1">
          Draft reading order “{createdReadingOrder.title}” was created.
        </AlertDescription>
        <Button
          colorScheme="green"
          onClick={() =>
            navigate(`/reading-orders/${createdReadingOrder.id}/comics`)
          }
          size="sm"
          variant="outline"
        >
          Review comics
        </Button>
      </Alert>
    );
  }

  return (
    <Box as="form" mt="5" onSubmit={handleSubmit}>
      <Stack align="end" direction={{ base: "column", lg: "row" }} spacing="4">
        <FormControl isRequired>
          <FormLabel>Title</FormLabel>
          <Input
            maxLength={255}
            onChange={(event) => setTitle(event.target.value)}
            value={title}
          />
        </FormControl>
        <FormControl isRequired maxW={{ lg: "220px" }}>
          <FormLabel>Franchise</FormLabel>
          <Select
            onChange={(event) => setFranchise(event.target.value)}
            placeholder="Select franchise"
            value={franchise}
          >
            {Object.keys(franchiseMapIndex).map((franchiseId) => (
              <option key={franchiseId} value={franchiseId}>
                {franchiseMapIndex[franchiseId]}
              </option>
            ))}
          </Select>
        </FormControl>
        <FormControl isRequired maxW={{ lg: "240px" }}>
          <FormLabel>Category</FormLabel>
          <Select
            onChange={(event) => setType(event.target.value)}
            placeholder="Select category"
            value={type}
          >
            {Object.keys(titleMapIndex).map((typeId) => (
              <option key={typeId} value={typeId}>
                {titleMapIndex[typeId]}
              </option>
            ))}
          </Select>
        </FormControl>
        <Button
          colorScheme="blue"
          isLoading={createMutation.isLoading}
          loadingText="Creating"
          minW="190px"
          type="submit"
        >
          Create reading order
        </Button>
      </Stack>
    </Box>
  );
};

const MIN_SEARCH_LENGTH = 3;

// Drop CMRO suffixes such as "[A Story]" so the default search hits the comic.
const defaultSearchTerm = (title) => title.split(" [")[0].trim();

const MatchComicModal = ({ cmroEntityId, comic, onClose }) => {
  const [searchTerm, setSearchTerm] = useState(() =>
    defaultSearchTerm(comic.title)
  );
  const [debouncedTerm, setDebouncedTerm] = useState(searchTerm);
  const queryClient = useQueryClient();
  const toast = useToast();
  const matchMutation = useMutation((comicId) =>
    matchAdminRawScrap(cmroEntityId, comic.rawScrapId, comicId)
  );

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedTerm(searchTerm.trim()), 500);

    return () => clearTimeout(timeout);
  }, [searchTerm]);

  const canSearch = debouncedTerm.length >= MIN_SEARCH_LENGTH;
  const searchQuery = useQuery(
    ["adminRawScrapComicSearch", debouncedTerm],
    // An empty franchise searches every franchise instead of defaulting to Marvel.
    () => getAdminComics("", debouncedTerm, "title", "asc"),
    { enabled: canSearch, refetchOnWindowFocus: false }
  );
  const results = searchQuery.data?.data || [];

  const handleMatch = async (selectedComic) => {
    try {
      const result = await matchMutation.mutateAsync(selectedComic.id);

      await queryClient.invalidateQueries(["adminRawScraps"]);
      toast({
        title: "Comic matched.",
        description: `#${comic.readingOrder} is now matched to ${selectedComic.title}; ${result.missingCount} remaining.`,
        status: result.missingCount === 0 ? "success" : "info",
        duration: 5000,
        isClosable: true,
      });
      onClose();
    } catch (error) {
      toast({
        title: "Unable to match comic.",
        description: error.response?.data?.message || "Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen onClose={onClose} scrollBehavior="inside" size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Match #{comic.readingOrder}
          <Text color="gray.500" fontSize="sm" fontWeight="normal">
            {comic.title}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl mb="4">
            <FormLabel>Search comics</FormLabel>
            <Input
              autoFocus
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="E.g: The Amazing Spider-Man (1963) #1"
              value={searchTerm}
            />
          </FormControl>
          {!canSearch && (
            <Text color="gray.500" fontSize="sm">
              Type at least {MIN_SEARCH_LENGTH} characters to search.
            </Text>
          )}
          {canSearch && searchQuery.isFetching && (
            <Flex justify="center" py="4">
              <ChakraSpinner aria-label="Searching comics" size="sm" />
            </Flex>
          )}
          {canSearch && !searchQuery.isFetching && results.length === 0 && (
            <Text color="gray.500" fontSize="sm">
              No comics found.
            </Text>
          )}
          {canSearch && !searchQuery.isFetching && results.length > 0 && (
            <List spacing="2">
              {results.map((result) => (
                <ListItem key={result.id}>
                  <Flex align="center" gap="3">
                    {result.thumbnail && (
                      <Image
                        alt=""
                        h="48px"
                        objectFit="cover"
                        rounded="sm"
                        src={result.thumbnail}
                        w="32px"
                      />
                    )}
                    <Box flex="1">
                      <Text fontSize="sm" fontWeight="semibold">
                        {result.title}
                      </Text>
                      <Text color="gray.500" fontSize="xs">
                        #{result.id}
                        {franchiseMapIndex[result.franchise] &&
                          ` · ${franchiseMapIndex[result.franchise]}`}
                        {result.digitalId &&
                          ` · Digital ID ${result.digitalId}`}
                      </Text>
                    </Box>
                    <Button
                      aria-label={`Match ${result.title}`}
                      colorScheme="blue"
                      isDisabled={matchMutation.isLoading}
                      isLoading={
                        matchMutation.isLoading &&
                        matchMutation.variables === result.id
                      }
                      onClick={() => handleMatch(result)}
                      size="sm"
                    >
                      Select
                    </Button>
                  </Flex>
                </ListItem>
              ))}
            </List>
          )}
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose} variant="ghost">
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const IncompleteScrapDetails = ({ scrap }) => {
  const [comicToMatch, setComicToMatch] = useState(null);
  const queryClient = useQueryClient();
  const toast = useToast();
  const rematchMutation = useMutation(() =>
    rematchAdminRawScraps(scrap.cmroEntityId)
  );

  const handleRematch = async () => {
    try {
      const result = await rematchMutation.mutateAsync();

      await queryClient.invalidateQueries(["adminRawScraps"]);
      toast({
        title: "Rematch completed.",
        description: `${result.newlyMatched} newly matched; ${result.missingCount} remaining.`,
        status: result.missingCount === 0 ? "success" : "info",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Unable to rematch comics.",
        description:
          error.response?.data?.message || "Please try the rematch again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <Flex align="center" justify="space-between" mb="3">
        <Text fontWeight="semibold">
          {scrap.missingComics.length} missing comic
          {scrap.missingComics.length === 1 ? "" : "s"}
        </Text>
        <Button
          colorScheme="blue"
          isLoading={rematchMutation.isLoading}
          loadingText="Rematching"
          onClick={handleRematch}
          size="sm"
          variant="outline"
        >
          Rematch
        </Button>
      </Flex>
      <List spacing="2">
        {scrap.missingComics.map((comic) => (
          <ListItem key={comic.rawScrapId}>
            <Button
              aria-label={`Match ${comic.title} manually`}
              mr="3"
              onClick={() => setComicToMatch(comic)}
              size="xs"
              variant="outline"
            >
              Match
            </Button>
            <Badge mr="2">#{comic.readingOrder}</Badge>
            <Link
              color="blue.400"
              href={`https://www.google.com/search?q=${encodeURIComponent(
                comic.title
              )}`}
              isExternal
            >
              {comic.title}
            </Link>
            {comic.digitalId && (
              <Text as="span" color="gray.500" fontSize="sm" ml="2">
                Digital ID:{" "}
                <Link
                  href={`https://read.marvel.com/#/book/${comic.digitalId}`}
                  isExternal
                >
                  {comic.digitalId}
                </Link>
              </Text>
            )}
          </ListItem>
        ))}
      </List>
      {comicToMatch && (
        <MatchComicModal
          cmroEntityId={scrap.cmroEntityId}
          comic={comicToMatch}
          onClose={() => setComicToMatch(null)}
        />
      )}
    </>
  );
};

const DeleteScrapGroup = ({ scrap }) => {
  const [isOpen, setIsOpen] = useState(false);
  const cancelRef = useRef();
  const queryClient = useQueryClient();
  const toast = useToast();
  const deleteMutation = useMutation(() =>
    deleteAdminRawScraps(scrap.cmroEntityId)
  );

  const closeDialog = () => {
    if (!deleteMutation.isLoading) {
      setIsOpen(false);
    }
  };

  const handleDelete = async () => {
    try {
      const result = await deleteMutation.mutateAsync();

      setIsOpen(false);
      await queryClient.invalidateQueries(["adminRawScraps"]);
      toast({
        title: "Raw scrap group deleted.",
        description: `${result.deletedCount} raw scrap${
          result.deletedCount === 1 ? "" : "s"
        } deleted.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Unable to delete raw scraps.",
        description: error.response?.data?.message || "Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <Flex
        borderColor="inherit"
        borderTopWidth="1px"
        justify="flex-end"
        mt="5"
        pt="4"
      >
        <Button
          colorScheme="red"
          leftIcon={<DeleteIcon />}
          onClick={() => setIsOpen(true)}
          size="sm"
          variant="outline"
        >
          Delete group
        </Button>
      </Flex>
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={closeDialog}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Delete raw scrap group?</AlertDialogHeader>
            <AlertDialogBody>
              This will permanently delete all {scrap.totalCount} raw scraps in
              “{scrap.cmroEntityName || `CMRO entity #${scrap.cmroEntityId}`}”.
              Existing comics will not be deleted.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                isDisabled={deleteMutation.isLoading}
                onClick={closeDialog}
                ref={cancelRef}
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                isLoading={deleteMutation.isLoading}
                loadingText="Deleting"
                ml="3"
                onClick={handleDelete}
              >
                Delete raw scraps
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

const RawScraps = () => {
  const scrapsQuery = useQuery(["adminRawScraps"], getAdminRawScraps, {
    refetchOnWindowFocus: false,
  });

  if (scrapsQuery.isLoading) {
    return <Spinner />;
  }

  if (scrapsQuery.isError) {
    return (
      <Alert status="error">
        <AlertIcon />
        <AlertDescription>Unable to load raw scraps.</AlertDescription>
      </Alert>
    );
  }

  const scraps = scrapsQuery.data?.data || [];

  if (scraps.length === 0) {
    return <Text>No raw scraps found.</Text>;
  }

  return (
    <Stack spacing="4">
      {scraps.map((scrap) => {
        const isComplete = isFullyMatched(scrap);

        if (isComplete) {
          return (
            <Accordion allowToggle key={scrap.cmroEntityId}>
              <AccordionItem
                border="1px solid"
                borderColor="inherit"
                rounded="md"
              >
                <h2>
                  <AccordionButton gap="4" p="5">
                    <ScrapSummary scrap={scrap} />
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel px="5" pb="5">
                  <CompletedScrapForm scrap={scrap} />
                  <DeleteScrapGroup scrap={scrap} />
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          );
        }

        return (
          <Accordion allowToggle key={scrap.cmroEntityId}>
            <AccordionItem
              border="1px solid"
              borderColor="inherit"
              rounded="md"
            >
              <h2>
                <AccordionButton gap="4" p="5">
                  <ScrapSummary scrap={scrap} />
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel px="5" pb="5">
                <IncompleteScrapDetails scrap={scrap} />
                <DeleteScrapGroup scrap={scrap} />
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        );
      })}
    </Stack>
  );
};

export default RawScraps;
